import Swal from "sweetalert2";
import i18n from "@/i18n.js";
import api from "@/api/api.js";
import { FISCAL_DOCUMENTS } from "@/utils/fiscalDocuments.js";

/**
 * Entregar un documento fiscal a un emisor, de principio a fin: subirlo, avisar
 * si su paquete no se lo va a mostrar, y en la 32/D dejar que el contador
 * confirme o corrija el sentido que el servidor propuso al leer el PDF.
 *
 * Vive aquí y no dentro de un componente porque se entrega desde dos sitios: el
 * recuadro de la ficha del emisor y los botones de la lista, que es una tabla
 * de jQuery y no puede montar un componente de React.
 *
 * Devuelve true si el documento quedó guardado.
 */
export const deliverFiscalDocument = async ({ companyId, type, file }) => {
  const { route, titleKey } = FISCAL_DOCUMENTS[type];
  const t = i18n.t.bind(i18n);

  const formData = new FormData();
  formData.append("file", file);

  let data;
  try {
    const response = await api.post(
      `/companies/${companyId}/${route}/latest`,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    data = response.data;
  } catch (error) {
    console.error("Error al subir el archivo:", error);
    await Swal.fire({
      icon: "error",
      title: t("ui.uploadFailed", { document: t(titleKey) }),
      text:
        error?.response?.data?.error ||
        error?.response?.data?.detail ||
        t("ui.uploadFailedHint"),
    });
    return false;
  }

  // El servidor guarda igual: se avisa de que el emisor no lo verá, no se impide.
  if (data?.warning) {
    await Swal.fire({
      icon: "info",
      title: t("ui.uploadedWithWarning"),
      text: data.warning,
    });
  }

  if (type === "complianceOpinion" && data?.id) {
    await confirmComplianceStatus(companyId, data.id, data.complianceStatus);
  }

  return true;
};

/**
 * El semáforo lo firma el contador, no el lector de PDF: se le muestra lo que
 * el servidor propuso y se guarda lo que él deje.
 */
const confirmComplianceStatus = async (companyId, documentId, proposed) => {
  const t = i18n.t.bind(i18n);

  const { value: status } = await Swal.fire({
    icon: proposed === "sin_determinar" ? "warning" : "question",
    title: t("ui.complianceProposedTitle"),
    text: t("ui.complianceProposedText", {
      status: t(`ui.compliance.${proposed}`),
    }),
    input: "select",
    inputValue: proposed,
    inputOptions: {
      positivo: t("ui.compliance.positivo"),
      negativo: t("ui.compliance.negativo"),
      sin_determinar: t("ui.compliance.sin_determinar"),
    },
    confirmButtonText: t("ui.confirm"),
  });

  if (!status || status === proposed) return;

  try {
    await api.patch(
      `/companies/${companyId}/complianceOpinion/${documentId}/status`,
      { status },
      { headers: { "Content-Type": "application/merge-patch+json" } },
    );
  } catch (error) {
    console.error("Error corrigiendo el sentido de la opinión:", error);
  }
};
